"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;
    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);
      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38]  border-b border-[#b08d38] bg-white  " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;
        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300  ">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white  transition-transform duration-300 text-[#000]" +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%]  text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Week 1–3: Paperwork, Coordination & Approvals ",
      content: (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px] sm:gap-[30px] lg:gap-[50px] items-start max-[1500px]:!grid-cols-[100%]">
            <div className="order-2 lg:order-1">

              <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Once the villa is handed over, the initial weeks are spent coordinating:
              </p>
              <ul className="grid gap-[16px] sm:gap-[20px] lg:gap-[30px] mt-[16px] lg:mt-0">
                <li className="grid grid-cols-[24px_auto] sm:grid-cols-[30px_auto] gap-x-[10px] sm:gap-x-[14px]">

                  <span className="mt-[4px] sm:mt-[6px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="black"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="black"
                      />
                    </svg>
                  </span>
                  <span className="text-[15px]/[22px] sm:text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                    Vendor discussions

                  </span>
                </li>

                <li className="grid grid-cols-[24px_auto] sm:grid-cols-[30px_auto] gap-x-[10px] sm:gap-x-[14px]">
                  <span className="mt-[4px] sm:mt-[6px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="black"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="black"
                      />
                    </svg>
                  </span>
                  <span className="text-[15px]/[22px] sm:text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                    Interior design planning

                  </span>
                </li>

                <li className="grid grid-cols-[24px_auto] sm:grid-cols-[30px_auto] gap-x-[10px] sm:gap-x-[14px]">
                  <span className="mt-[4px] sm:mt-[6px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="black"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="black"
                      />
                    </svg>
                  </span>
                  <span className="text-[15px]/[22px] sm:text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                    Electrical and plumbing approvals

                  </span>
                </li>

                <li className="grid grid-cols-[24px_auto] sm:grid-cols-[30px_auto] gap-x-[10px] sm:gap-x-[14px]">
                  <span className="mt-[4px] sm:mt-[6px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="black"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="black"
                      />
                    </svg>
                  </span>
                  <span className="text-[15px]/[22px] sm:text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                    Material finalisation

                  </span>
                </li>


              </ul>

              <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">This phase often feels invisible-but it sets the tone for everything that follows.

              </p>
            </div>

            <div className="order-1 lg:order-2 w-full">
              <div className="overflow-hidden border border-[#b08d38]/30 max-w-[500px]">
                <Image
                  src={`${ASSET_PREFIX}/images/the-real-timeline-of-setting-up-a-villa-image.webp`}
                  alt="Gallery"
                  width={330}
                  height={330}
                  className="w-full object-cover h-[100%] "
                />
              </div>
            </div>
          </div>

        </>
      ),
    },
    // {
    //   id: 2,
    //   title: "Food, Garden & Healthy Living",
    //   content: (
    //     <>
    //       <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px] sm:gap-[30px] lg:gap-[50px] items-start max-[1500px]:!grid-cols-[100%]">
    //         <div className="order-2 lg:order-1  w-full">
    //           <div className="rounded-[16px] sm:rounded-[20px] overflow-hidden border border-[#b08d38]/30 max-w-[500px]">
    //             <Image
    //               src={`${ASSET_PREFIX}/images/21-beautiful-02.webp`}
    //               alt="Self-care & Wellness"
    //               width={900}
    //               height={900}
    //               className="w-full object-cover h-[100%]"
    //             />
    //           </div>
    //         </div>
    //         <div className="order-1 lg:order-2">
    //           <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Your Own Organic Veggie Patch</b> Grow tomatoes, chilies,
    //                 leafy greens, herbs and more - fresh from home
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>A Mini Herb Garden</b> Mint, rosemary, basil, lemongrass -
    //                 flavourful ingredients right outside your door.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Smoothie Garden for Healthy Living</b> Spinach, kale and
    //                 microgreens make superfoods easy and fresh.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Outdoor Breakfast Rituals</b> tart the day with sunlight,
    //                 nature and a wholesome meal.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>DIY Barbeque Evenings</b> Grill veggies, paneer, chicken or
    //                 seafood - the Sky Garden becomes a festive dinner zone.
    //               </span>
    //             </li>
    //           </ul>
    //         </div>
    //       </div>
    //     </>
    //   ),
    // },

    // {
    //   id: 3,
    //   title: "Friends, Family & Social Moments",
    //   content: (
    //     <>
    //       <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px] sm:gap-[30px] lg:gap-[50px] items-start max-[1500px]:!grid-cols-[100%]">
    //         <div className="order-2 lg:order-1">
    //           <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>A Beautiful Open-Air Dining Space</b> Host brunches,
    //                 candlelight dinners or family meals under the sky.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Sundowner Get-Togethers</b> Golden hour, fairy lights, music
    //                 and laughter - perfect for hosting.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Birthday & Celebration Spot</b> A magical backdrop for
    //                 intimate celebrations with family and friends.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Mini Movie Nights</b> Projector + cushions + night sky =
    //                 unforgettable cinema at home.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b> Kids’ Play Zone</b> A safe outdoor space for crafts, games,
    //                 gardening, or mini tents.
    //               </span>
    //             </li>
    //           </ul>
    //         </div>

    //         <div className="order-1 lg:order-2 w-full">
    //           <div className="rounded-[16px] sm:rounded-[20px] overflow-hidden border border-[#b08d38]/30 max-w-[500px]">
    //             <Image
    //               src={`${ASSET_PREFIX}/images/21-beautiful-03.webp`}
    //               alt="Self-care & Wellness"
    //               width={900}
    //               height={900}
    //               className="w-full object-cover h-[100%]"
    //             />
    //           </div>
    //         </div>


    //       </div>
    //     </>
    //   ),
    // },
    // {
    //   id: 4,
    //   title: "Hobbies, Work & Creativity",
    //   content: (
    //     <>
    //       <div className="grid grid-cols-1 lg:grid-cols-2 gap-[24px] sm:gap-[30px] lg:gap-[50px] items-start max-[1500px]:!grid-cols-[100%]">

    //         <div className="order-2 lg:order-1  w-full">
    //           <div className="rounded-[16px] sm:rounded-[20px] overflow-hidden border border-[#b08d38]/30 max-w-[500px]">
    //             <Image
    //               src={`${ASSET_PREFIX}/images/21-beautiful-04.webp`}
    //               alt="Self-care & Wellness"
    //               width={900}
    //               height={900}
    //               className="w-full object-cover h-[100%]"
    //             />
    //           </div>
    //         </div>

    //         <div className="order-1 lg:order-2">
    //           <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Outdoor Work-From-Home Corner</b> A refreshing alternative to
    //                 desk work - productivity with natural light.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Photography & Content Creation Zone</b> Natural light,
    //                 greenery and scenic sky - perfect for reels and photography.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Art & Craft Studio</b> Paint, sketch or craft with
    //                 inspiration all around you.
    //               </span>
    //             </li>
    //             <li className="grid grid-cols-[30px_auto]">
    //               <span className="mt-[6px]">
    //                 <svg
    //                   xmlns="https://www.w3.org/2000/svg"
    //                   width="16"
    //                   height="20"
    //                   viewBox="0 0 16 20"
    //                   fill="none">
    //                   <path
    //                     d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
    //                     fill="black"
    //                   />
    //                   <path
    //                     d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
    //                     fill="black"
    //                   />
    //                 </svg>
    //               </span>
    //               <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
    //                 <b>Music Practice Area</b> Guitar, singing, flute - the
    //                 acoustics and ambience are beautiful.
    //               </span>
    //             </li>
    //           </ul>
    //         </div>
    //       </div>
    //     </>
    //   ),
    // },
    {
      id: 2,
      title: "Month 1–2: Kitchen, Wardrobes & Core Interiors ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">This is when the heavy interior work begins: </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Modular kitchen installation

              </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Wardrobes and storage units

              </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Carpentry measurements and revisions


              </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">Delays are common here due to material lead times and vendor dependencies.
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: "Month 2–3: Electricals, ACs & Automation  ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Next comes functionality: </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Air conditioning installation

              </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Electrical fittings  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Smart home wiring and automation </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">Coordination between vendors becomes critical-and stressful.

          </p>
        </>
      ),
    },
    {
      id: 4,
      title: "Month 3–4: Bathrooms & Glass Partitions  ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Bathrooms may look complete, but final usability comes later: </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Shower enclosures

              </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Glass partitions </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Fixture alignment and waterproofing checks </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">Small delays here can push the entire timeline forward.


          </p>
        </>
      ),
    },
    {
      id: 5,
      title: "Month 4–5: Painting, False Ceilings & Touch-Ups",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">This stage is about refinement: </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Final paint coats  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                False ceiling completion  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Patchwork and finishing </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">It’s also where homeowners realise how many “small things” were missing.


          </p>
        </>
      ),
    },

    {
      id: 6,
      title: "Month 5–6: Waterproofing, Corrections & Callbacks ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">The final stretch includes:  </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Terrace waterproofing   </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Electrical corrections   </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Vendor callbacks and snag fixes  </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">Only after this phase does the villa feel move-in ready.



          </p>
        </>
      ),
    },
    {
      id: 7,
      title: "So When Do You Actually Move In? ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">For most villa buyers, 6 months after handover is when life truly begins inside the home.   </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">And during this period, homeowners deal with: </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Multiple vendors  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Rising costs   </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Constant follow-ups  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Living in rented or temporary spaces   </span>
            </li>
          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">This is the reality of villa ownership-one that the industry rarely talks about.




          </p>
        </>
      ),
    },
    {
      id: 8,
      title: "How +Willa Changes This Entire Timeline",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">At +Willa, we asked a simple question: </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Why should a homeowner wait months to live in a home they’ve already bought? </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">So we reversed the process.  </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Instead of handing over a structure and asking customers to “finish it later,” +Willa delivers a villa that is ready from Day 1. </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Every +Willa home comes:  </p>
          <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Fully finished </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Fully furnished  </span>
            </li>
            <li className="grid grid-cols-[30px_auto]">
              <span className="mt-[6px]">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="16"
                  height="20"
                  viewBox="0 0 16 20"
                  fill="none">
                  <path
                    d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                    fill="black"
                  />
                  <path
                    d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                    fill="black"
                  />
                </svg>
              </span>
              <span className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
                Fully automated
              </span>
            </li>

          </ul>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] mt-[20px]">No post-handover vendors. </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] ">No coordination headaches.  </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717] ">No waiting period. </p>
        </>
      ),
    },
    {
      id: 9,
      title: "Zero Move-In Time. Truly. ",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">What usually takes 4–6 months after handover is already done at +Willa.  </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">So when you receive the keys, you don’t receive a project.
          </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">You receive a home.  </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">+Willa compresses the entire setup timeline into zero move-in time.
          </p>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">Because a villa should be lived in-not worked on.
          </p>


        </>
      ),
    },
  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};

export default function BeautifulWays() {
  const data = [
    {
      image: `/images/blogs/the-real-timeline-of-setting-up-a-villa.webp`,
      title: "The Real Timeline of Setting Up a Villa (And Why Most Homes Aren’t Ready When You Get the Keys) ",
      date: "18 Dec 2025",

      content1: (
        <>
          <p className="text-[18px] leading-[26px] mb-[20px] 2xl:text-[20px] 2xl:leading-[28px] font-medium text-gray-900">
            Buying a villa is often seen as the final milestone in the homebuying journey.

          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            But for most homeowners, it’s actually just the beginning.

          </p>


          <p className="text-[18px] leading-[26px] mb-[20px] 2xl:text-[20px] 2xl:leading-[28px] font-medium text-gray-900">
            What no one clearly explains is this:

          </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            a villa handover does not mean a villa is ready to live in. </p>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            In reality, families spend 4 to 6 additional months setting up the home before they can truly move in. Let’s break down what really happens after you receive the keys.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="sticky top-[130px] z-[2] ">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog list"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px] ">
            <p className="my-[20px] text-[20px] leading-[28px] font-light max-xxl:text-[18px] max-xxl:leading-[26px] group-hover:text-[#8e722d] transition-colors duration-300 max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>
          {/* 
          <div className="p-[30px] bg-[#e8e8e8] rounded-[30px] mt-[50px]">
            <h3 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[15px]">
              Why the +Willa Sky Garden Makes All This Possible
            </h3>
            <p className="mb-[15px]">
              Because it’s not just a terrace - it’s an engineered living space:
            </p>
            <ul className="grid gap-[20px] md:gap-[30px] my-[30px]">
              {[
                "Zero leakage system",
                "Biofabric for healthy plant growth",
                "PU waterproofin",
                "Engineered slope + drain cells",
                "Private seating deck with shaded panels",
                "Lighting, irrigation and power points pre-planned",
                "Ready-to-use from Day 1",
              ].map((text, i) => (
                <li key={i} className="grid grid-cols-[30px_auto]">
                  <span className="mt-[4px]">
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none">
                      <path
                        d="M4.36783 16.2162C6.04375 13.5794 7.03023 10.0703 7.04695 6.47255C7.06563 2.41358 5.78705 0.00887961 3.78755 2.79644e-05C1.68674 -0.0098072 0.290139 2.57487 0.267518 7.39706C0.251782 10.9112 0.891072 13.9935 2.02901 16.233C1.65527 17.0926 0.828127 17.7387 0 18.0485L0.665846 19.932C1.55889 19.4176 2.37423 18.7409 3.11187 17.9C4.17506 19.214 5.4979 19.9536 6.98499 19.9605L7.61837 19.9634L7.62821 17.8754C7.62821 17.8754 5.25005 18.0584 4.36685 16.2162M2.01131 7.21412C2.02606 3.8623 2.72633 2.2572 3.73739 2.26212C4.70813 2.26703 5.31496 3.7964 5.30316 6.43911C5.28939 9.35622 4.54486 12.1592 3.28595 14.3859C2.46274 12.531 1.9995 10.0211 2.01229 7.21412"
                        fill="#171717"
                      />
                      <path
                        d="M12.3295 16.2529C14.0054 13.6161 14.9919 10.1069 15.0086 6.50917C15.0273 2.4502 13.7497 0.0455007 11.7502 0.0366491C9.64939 0.0268139 8.2518 2.6115 8.23017 7.43368C8.21443 10.9478 8.85372 14.0301 9.99166 16.2696C9.61792 17.1292 8.79077 17.7754 7.96265 18.0852L8.62849 19.9686C9.52153 19.4542 10.3369 18.7776 11.0745 17.9366C12.1377 19.2506 13.4605 19.9902 14.9476 19.9971L15.581 20.0001L15.5909 17.9121C15.5909 17.9121 13.2127 18.095 12.3295 16.2529ZM9.97395 7.24976C9.98871 3.89794 10.689 2.29284 11.7 2.29775C12.6708 2.30267 13.2776 3.83204 13.2658 6.47475C13.252 9.39186 12.5075 12.1949 11.2486 14.4216C10.4254 12.5667 9.96215 10.0567 9.97494 7.24976"
                        fill="#171717"
                      />
                    </svg>
                  </span>
                  <span className=" ">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mb-[15px]">
              Your Sky Garden is designed so you don’t have to build or fix
              anything - just enjoy.
            </p>
          </div> */}
        </div>
      ))}
    </div>
  );
}
